/**
 * CollapsibleSection - Expandable section container for deck statistics
 *
 * Used in the deck info panel to organize statistics into
 * collapsible groups with optional status indicators.
 */
import type { ReactElement, ReactNode } from 'react';

import clsx from 'clsx';
import { FaCheck, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';

/** Status indicator types for collapsed section headers */
export type SectionStatus = 'valid' | 'warning' | 'error' | 'neutral' | 'none';

export interface CollapsibleSectionProps<T extends string = string> {
  /** Unique identifier for the section */
  id: T;
  /** Section heading label */
  label: string;
  /** Whether the section is collapsed */
  isCollapsed: boolean;
  /** Callback when section is toggled */
  onToggle: (id: T) => void;
  /** Section content */
  children: ReactNode;
  /** Status indicator shown when collapsed (optional) */
  status?: SectionStatus;
  /**
   * When true, hides the expand/collapse arrow and disables interaction.
   * Used for sections that auto-collapse and shouldn't be expanded (e.g., valid validation).
   */
  lockedCollapsed?: boolean;
}

/** Status indicator configuration */
const STATUS_CONFIG: Record<SectionStatus, { icon: ReactElement | null; className: string; ariaLabel: string } | null> = {
  valid: {
    icon: <FaCheck className="h-2.5 w-2.5" />,
    className: 'text-emerald-400 bg-emerald-500/20',
    ariaLabel: 'Valid',
  },
  warning: {
    icon: <FaExclamationTriangle className="h-2.5 w-2.5" />,
    className: 'text-amber-400 bg-amber-500/20',
    ariaLabel: 'Has warnings',
  },
  error: {
    icon: <FaTimes className="h-2.5 w-2.5" />,
    className: 'text-red-400 bg-red-500/20',
    ariaLabel: 'Has errors',
  },
  neutral: {
    icon: null,
    className: 'text-zinc-400 bg-zinc-500/20',
    ariaLabel: 'No format selected',
  },
  none: null,
};

export default function CollapsibleSection<T extends string = string>({
  id,
  label,
  isCollapsed,
  onToggle,
  children,
  status = 'none',
  lockedCollapsed = false,
}: CollapsibleSectionProps<T>): ReactElement {
  const statusConfig = status !== 'none' ? STATUS_CONFIG[status] : null;
  const showStatusIndicator = isCollapsed && statusConfig;
  const isInteractive = !lockedCollapsed;

  return (
    <div className="border-b border-zinc-800">
      {isInteractive ? (
        <button
          type="button"
          onClick={() => onToggle(id)}
          className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-zinc-800/30"
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? <IoChevronForward className="h-3 w-3 text-zinc-500" /> : <IoChevronDown className="h-3 w-3 text-zinc-500" />}
          <span className="flex-1 text-xs font-medium tracking-wider text-zinc-500 uppercase">{label}</span>
          {showStatusIndicator && (
            <span
              className={clsx('flex items-center justify-center rounded-full p-1', statusConfig.className)}
              aria-label={statusConfig.ariaLabel}
            >
              {statusConfig.icon}
            </span>
          )}
        </button>
      ) : (
        <div className="pointer-events-none flex w-full items-center gap-2 px-4 py-3 text-left">
          <span className="flex-1 text-xs font-medium tracking-wider text-zinc-500 uppercase">{label}</span>
          {showStatusIndicator && (
            <span
              className={clsx('flex items-center justify-center rounded-full p-1', statusConfig.className)}
              aria-label={statusConfig.ariaLabel}
            >
              {statusConfig.icon}
            </span>
          )}
        </div>
      )}
      {!isCollapsed && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}
